import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Stack, Chip,
  IconButton, TextField, MenuItem, Tooltip, Avatar, LinearProgress,
  Divider, Paper
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon,
  Description as CdcIcon, ArrowBack as ArrowBackIcon,
  Business as BusinessIcon, Construction as ToolIcon,
  CheckCircle as SignedIcon, PendingActions as PendingIcon
} from '@mui/icons-material';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { crmApi } from '../services/crmApi';
import { CrmCdc, CdcStatus, ProductGamme, CrmOrganisation, CrmContact, CrmOpportunity } from '../types/crm';

const emptyCdc = (): Partial<CrmCdc> => ({
  title: '',
  dateCreation: new Date().toISOString().split('T')[0],
  status: 'DRAFT',
  gamme: 'URBANIA',
  unitsCount: 100,
  estimatedBudget: 5000000,
  technicalConstraints: 'Profilés PVC haute isolation, double vitrage 4/16/4 faible émissivité avec argon, classement AEV A*4 E*7B V*A3.'
});

const getStatusColor = (status: CdcStatus): 'default' | 'primary' | 'info' | 'success' | 'warning' | 'error' => {
  switch (status) {
    case 'DRAFT': return 'default';
    case 'SUBMITTED': return 'info';
    case 'VALIDATED': return 'primary';
    case 'SIGNED': return 'success';
    case 'REJECTED': return 'error';
    default: return 'default';
  }
};

const getGammeColor = (gamme: ProductGamme): 'primary' | 'secondary' | 'warning' | 'info' => {
  switch (gamme) {
    case 'URBANIA': return 'primary';
    case 'FUTURA': return 'secondary';
    case 'DOORIA': return 'warning';
    default: return 'info';
  }
};

export const CdcPage: React.FC = () => {
  const { t } = useLanguage();

  const [view, setView] = useState<'list' | 'form'>('list');
  const [viewMode, setViewMode] = useDefaultViewMode();
  const [cdcList, setCdcList] = useState<CrmCdc[]>([]);
  const [organisations, setOrganisations] = useState<CrmOrganisation[]>([]);
  const [contacts, setContacts] = useState<CrmContact[]>([]);
  const [opportunities, setOpportunities] = useState<CrmOpportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<CrmCdc>>(emptyCdc());
  const [isEditing, setIsEditing] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cData, oData, ctData, pData] = await Promise.all([
        crmApi.getCdcs(),
        crmApi.getOrganisations(),
        crmApi.getContacts(),
        crmApi.getOpportunities()
      ]);
      setCdcList(cData);
      setOrganisations(oData);
      setContacts(ctData);
      setOpportunities(pData);
    } catch (err) {
      console.error('Error loading CRM CDC:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openNew = () => {
    setFormData(emptyCdc());
    setIsEditing(false);
    setView('form');
  };

  const openEdit = (cdc: CrmCdc) => {
    setFormData({ ...cdc });
    setIsEditing(true);
    setView('form');
  };

  const backToList = () => {
    setView('list');
  };

  const handleDelete = async (cdc: CrmCdc) => {
    const cdcId = (cdc.id || cdc.CDC_IDF)!;
    const confirmed = await blobticMessage.confirm({
      message: t('crm.cdc.confirmDelete', 'Êtes-vous sûr de vouloir supprimer ce cahier des charges ?'),
      title: t('common.confirm', 'Confirmation')
    });

    if (confirmed) {
      const res = await crmApi.deleteCdc(cdcId);
      if (res.success) {
        blobticMessage.success(t('crm.cdc.deleteSuccess', 'Cahier des charges supprimé avec succès.'));
        loadData();
      } else {
        blobticMessage.error(res.message || t('common.error', 'Erreur lors de la suppression.'));
      }
    }
  };

  const handleValidate = (): string | null => {
    if (!formData.title?.trim()) {
      return t('crm.cdc.titleRequired', 'Le titre du cahier des charges est obligatoire.');
    }
    return null;
  };

  const handleDataChange = () => {
    backToList();
    loadData();
  };

  if (view === 'form') {
    const formPayload = {
      ...formData,
      CDC_IDF: formData.id || formData.CDC_IDF,
      CDC_CODE: formData.code || formData.CDC_CODE,
      CDC_TITLE: formData.title,
      COP_IDF: formData.projectId || formData.COP_IDF,
      COR_IDF: formData.organisationId || formData.COR_IDF,
      CCT_IDF: formData.contactId || formData.CCT_IDF,
      CDC_DATE_CREATION: formData.dateCreation,
      CDC_DATE_SIGNATURE: formData.dateSignature,
      CDC_STATUS: formData.status,
      CDC_GAMME: formData.gamme,
      CDC_UNITS_COUNT: formData.unitsCount,
      CDC_ESTIMATED_BUDGET: formData.estimatedBudget,
      CDC_TECHNICAL_CONSTRAINTS: formData.technicalConstraints,
      CDC_DOC_PATH: formData.docPath,
    };

    return (
      <GenericPage<any>
        title={isEditing ? t('crm.cdc.editTitle', 'Modifier le Cahier des Charges') : t('crm.cdc.newTitle', 'Nouveau Cahier des Charges')}
        icon={<CdcIcon />}
        footerEnabled={true}
        footerInfo={formData.last_updt ? { lastUpdate: formData.last_updt, userLib: formData.user_lib } : undefined}
        additionalActions={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={backToList}
          >
            {t('common.backToList', 'Retour à la liste')}
          </Button>
        }
        crudConfig={{ entityKey: 'Blobtic_CRM,crm_cdc', primaryKey: 'CDC_IDF' }}
        data={formPayload}
        onDataChange={handleDataChange}
        onValidate={handleValidate}
      >
        <Box sx={{ p: 1 }}>
          <Grid container spacing={3}>
            {/* Section 1: Identification */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CdcIcon color="primary" /> {t('crm.cdc.secIdent', 'Identification du Document')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={t('common.code', 'Code CDC')}
                      value={formData.code || t('common.autoGenerated', '(Automatique)')}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <TextField
                      label={t('crm.cdc.titleField', 'Titre / Objet du Cahier des Charges')}
                      value={formData.title || ''}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      fullWidth
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('crm.cdc.project', 'Projet / Affaire Associée')}
                      value={formData.projectId || ''}
                      onChange={(e) => setFormData({ ...formData, projectId: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">{t('crm.cdc.selectProject', '-- Sélectionner un projet --')}</MenuItem>
                      {opportunities.map((opp) => (
                        <MenuItem key={opp.id || opp.COP_IDF} value={opp.id || opp.COP_IDF}>
                          {opp.title} ({opp.stage})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('crm.cdc.organisation', 'BET / Architecte Prescripteur')}
                      value={formData.organisationId || ''}
                      onChange={(e) => setFormData({ ...formData, organisationId: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">{t('crm.cdc.selectOrg', '-- Sélectionner l\'organisation --')}</MenuItem>
                      {organisations.map((org) => (
                        <MenuItem key={org.id || org.COR_IDF} value={org.id || org.COR_IDF}>
                          {org.name} ({org.type})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label={t('crm.cdc.contact', 'Contact Prescripteur Clé')}
                      value={formData.contactId || ''}
                      onChange={(e) => setFormData({ ...formData, contactId: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="">{t('crm.cdc.selectContact', '-- Sélectionner un contact --')}</MenuItem>
                      {contacts.map((c) => (
                        <MenuItem key={c.id || c.CCT_IDF} value={c.id || c.CCT_IDF}>
                          {c.firstName} {c.lastName} ({c.company || 'Contact'})
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Section 2: Spécifications Gammes & Métré */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ToolIcon color="primary" /> {t('crm.cdc.secSpecs', 'Préconisations Techniques Gammes & Produits')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      select
                      label={t('crm.cdc.gamme', 'Gamme de Produits')}
                      value={formData.gamme || 'URBANIA'}
                      onChange={(e) => setFormData({ ...formData, gamme: e.target.value as ProductGamme })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="URBANIA">{t('crm.gamme.urbania', 'Gamme Urbania (Fenêtres & Coulissants PVC)')}</MenuItem>
                      <MenuItem value="FUTURA">{t('crm.gamme.futura', 'Gamme Futura (Haute Performance Thermique)')}</MenuItem>
                      <MenuItem value="DOORIA">{t('crm.gamme.dooria', 'Gamme Dooria (Portes d\'Entrée Sécurisées)')}</MenuItem>
                      <MenuItem value="MIXTE">{t('crm.gamme.mixte', 'Solutions Mixtes & Sur-Mesure')}</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={t('crm.cdc.unitsCount', 'Nombre d\'Unités / Ouvrants')}
                      type="number"
                      value={formData.unitsCount ?? 0}
                      onChange={(e) => setFormData({ ...formData, unitsCount: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      label={t('crm.cdc.estimatedBudget', 'Budget Estimatif (DZD)')}
                      type="number"
                      value={formData.estimatedBudget ?? 0}
                      onChange={(e) => setFormData({ ...formData, estimatedBudget: Number(e.target.value) })}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      select
                      label={t('common.status', 'Statut du CDC')}
                      value={formData.status || 'DRAFT'}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as CdcStatus })}
                      fullWidth
                      size="small"
                    >
                      <MenuItem value="DRAFT">{t('crm.cdc.statusDraft', 'Brouillon interne')}</MenuItem>
                      <MenuItem value="SUBMITTED">{t('crm.cdc.statusSubmitted', 'Soumis au BET / Client')}</MenuItem>
                      <MenuItem value="VALIDATED">{t('crm.cdc.statusValidated', 'Validé techniquement')}</MenuItem>
                      <MenuItem value="SIGNED">{t('crm.cdc.statusSigned', 'Officiellement Signé / Intégré')}</MenuItem>
                      <MenuItem value="REJECTED">{t('crm.cdc.statusRejected', 'Rejeté / Concurrence')}</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('crm.cdc.dateCreation', 'Date de Création')}
                      type="date"
                      value={formData.dateCreation || ''}
                      onChange={(e) => setFormData({ ...formData, dateCreation: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t('crm.cdc.dateSignature', 'Date de Signature / Validation')}
                      type="date"
                      value={formData.dateSignature || ''}
                      onChange={(e) => setFormData({ ...formData, dateSignature: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t('crm.cdc.technicalConstraints', 'Exigences Techniques (AEV, Acoustique, Thermique, Vitrage)')}
                      value={formData.technicalConstraints || ''}
                      onChange={(e) => setFormData({ ...formData, technicalConstraints: e.target.value })}
                      multiline
                      rows={4}
                      fullWidth
                      placeholder={t('crm.cdc.constraintsPlaceholder', 'Indiquez les contraintes thermiques (Uw), acoustiques (Rw+Ctr), classement AEV, type de renforts acier, motorisations, etc.')}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </GenericPage>
    );
  }

  // List View
  return (
    <GenericPage
      title={t('crm.cdc.title', 'Cahiers des Charges (CDC) & Préconisations')}
      icon={<CdcIcon />}
      footerEnabled={false}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onNew={openNew}
    >
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {viewMode === 'grid' ? (
        <Grid container spacing={2.5}>
          {cdcList.map((cdc) => {
            const cdcId = cdc.id || cdc.CDC_IDF;
            return (
              <Grid item xs={12} sm={6} md={4} key={cdcId}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    borderRadius: 2.5,
                    transition: 'all 0.25s ease-in-out',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      borderColor: 'primary.main',
                      transform: 'translateY(-3px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
                        <CdcIcon />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap title={cdc.title}>
                          {cdc.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {cdc.code}
                        </Typography>
                      </Box>
                      <Chip
                        label={t(cdc.status, cdc.status)}
                        color={getStatusColor(cdc.status)}
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    <Stack spacing={1} sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={`${t('crm.cdc.gamme', 'Gamme')} ${t(cdc.gamme, cdc.gamme)}`}
                          color={getGammeColor(cdc.gamme)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                        <Chip
                          label={`${cdc.unitsCount} ${t('crm.cdc.units', 'unités')}`}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>

                      {cdc.projectTitle && (
                        <Typography variant="body2" fontWeight={600} color="text.primary" noWrap>
                          📁 {cdc.projectTitle}
                        </Typography>
                      )}

                      {cdc.organisationName && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          🏢 {t('crm.cdc.prescripteur', 'BET/Archi')}: {cdc.organisationName}
                        </Typography>
                      )}

                      <Typography variant="h6" color="primary.main" fontWeight={700} sx={{ pt: 0.5 }}>
                        {(cdc.estimatedBudget || 0).toLocaleString()} DZD
                      </Typography>
                    </Stack>

                    {cdc.technicalConstraints && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          bgcolor: 'action.hover',
                          p: 1,
                          borderRadius: 1
                        }}
                      >
                        {cdc.technicalConstraints}
                      </Typography>
                    )}
                  </CardContent>

                  <Box sx={{ px: 2.5, py: 1.5, bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {cdc.dateSignature ? `${t('crm.cdc.signedOn', 'Signé le')}: ${new Date(cdc.dateSignature).toLocaleDateString()}` : t('crm.cdc.notSigned', 'Non signé')}
                    </Typography>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={t('common.edit', 'Modifier')}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(cdc)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete', 'Supprimer')}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(cdc)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <GenericTable
          columns={[
            { id: 'code', label: t('common.code', 'Code'), width: 130 },
            { id: 'title', label: t('crm.cdc.titleField', 'Titre CDC'), minWidth: 220 },
            {
              id: 'gamme',
              label: t('crm.cdc.gamme', 'Gamme'),
              width: 120,
              chip: true,
              chipColorMap: {
                URBANIA: 'primary',
                FUTURA: 'secondary',
                DOORIA: 'warning',
                MIXTE: 'info'
              }
            },
            { id: 'unitsCount', label: t('crm.cdc.unitsCount', 'Unités'), width: 100 },
            { id: 'estimatedBudget', label: t('crm.cdc.estimatedBudget', 'Budget (DZD)'), width: 150, format: (val: any) => `${Number(val || 0).toLocaleString()} DZD` },
            { id: 'projectTitle', label: t('crm.cdc.project', 'Projet'), width: 180 },
            { id: 'organisationName', label: t('crm.cdc.organisation', 'Prescripteur'), width: 180 },
            {
              id: 'status',
              label: t('common.status', 'Statut'),
              width: 130,
              chip: true,
              chipColorMap: {
                DRAFT: 'default',
                SUBMITTED: 'info',
                VALIDATED: 'primary',
                SIGNED: 'success',
                REJECTED: 'error'
              }
            },
          ]}
          data={cdcList}
          onEdit={openEdit}
          onDelete={handleDelete}
          searchPlaceholder={t('crm.cdc.search', 'Rechercher un CDC, gamme, projet...')}
          filterColumns={['code', 'title', 'gamme', 'projectTitle', 'organisationName', 'status']}
          maxHeight="calc(100vh - 270px)"
        />
      )}
    </GenericPage>
  );
};

export default CdcPage;
